import {
  createAdminBattlePassSeason,
  deleteAdminBattlePassSeason,
  endAdminBattlePassSeason,
  fetchAdminBattlePassCosmetic,
  fetchAdminBattlePassPickers,
  fetchAdminBattlePassSeason,
  fetchAdminBattlePassSeasons,
  patchAdminBattlePassLevel,
  patchAdminBattlePassSeason,
  publishAdminBattlePassSeason,
} from "./battle-pass/api";
import type {
  BattlePassLevelAdmin,
  BattlePassRewardKind,
  BattlePassSeasonAdmin,
} from "./battle-pass/types";
import {
  mountAdminBattlePassItemCreator,
  type ShopCreatorEditItem,
} from "./admin-shop-creator";
import { createModalCloseButton } from "./modal-close-button";
import type { CosmeticCategory, ShopUnlockType } from "../lib/cloud";

export type AdminBattlePassApi = { refresh: () => Promise<void> };

const REWARD_KINDS: Array<{ id: BattlePassRewardKind; label: string }> = [
  { id: "none", label: "None" },
  { id: "dots", label: "Dots" },
  { id: "exclusive_item", label: "Battle Pass exclusive" },
  { id: "shop_item", label: "Existing Shop item" },
  { id: "free_crate", label: "Free crate" },
];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function field(label: string, input: HTMLElement): HTMLElement {
  const row = el("label", "formRow adminBpField");
  row.append(el("span", "formLabel", label), input);
  return row;
}

function numInput(value: number, min = 0, max = 1_000_000): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "number";
  input.className = "formInput";
  input.value = String(value);
  input.min = String(min);
  input.max = String(max);
  return input;
}

function textInput(value: string, placeholder = ""): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "text";
  input.className = "formInput";
  input.value = value;
  input.placeholder = placeholder;
  return input;
}

export function mountAdminBattlePass(root: HTMLElement): AdminBattlePassApi {
  root.classList.add("adminBp");
  let seasons: BattlePassSeasonAdmin[] = [];
  let selected: BattlePassSeasonAdmin | null = null;
  let levels: BattlePassLevelAdmin[] = [];
  let warnings: string[] = [];
  let tab: "details" | "xp" | "levels" | "preview" = "details";
  let editLevel = 1;
  let status = "";
  let pickers = {
    exclusives: [] as Array<{ id: string; name: string; imageData: string }>,
    shop: [] as Array<{ id: string; name: string; imageData: string }>,
    crates: [] as Array<{ id: string; name: string; imageData: string }>,
  };

  function upsertExclusiveInPickers(cosmetic: { id: string; name: string; imageData: string }) {
    const rest = pickers.exclusives.filter((c) => c.id !== cosmetic.id);
    pickers.exclusives = [cosmetic, ...rest];
  }

  function openExclusiveItemCreator(input: {
    levelId: string;
    cosmeticSelect: HTMLSelectElement;
    kindSelect: HTMLSelectElement;
    editCosmeticId?: string | null;
  }) {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen campaignAdminItemCreatorModal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox campaignAdminItemCreatorPanel";
    const title = document.createElement("h2");
    title.textContent = input.editCosmeticId
      ? "Edit Battle Pass exclusive"
      : "Create Battle Pass exclusive";
    const mount = document.createElement("div");
    mount.className = "campaignAdminItemCreatorMount";
    const close = () => {
      mount.replaceChildren();
      modal.remove();
    };
    box.append(createModalCloseButton(close), title, mount);
    modal.appendChild(box);
    document.body.appendChild(modal);

    const creator = mountAdminBattlePassItemCreator(mount, {
      permissions: { publish: true, overwrite: false },
      unlockBattlePassLevelId: input.levelId,
      onBattlePassItemPublished: (created) => {
        upsertExclusiveInPickers(created);
        input.kindSelect.value = "exclusive_item";
        input.cosmeticSelect.replaceChildren();
        const empty = document.createElement("option");
        empty.value = "";
        empty.textContent = "—";
        input.cosmeticSelect.appendChild(empty);
        for (const item of pickers.exclusives) {
          const o = document.createElement("option");
          o.value = item.id;
          o.textContent = item.name;
          if (item.id === created.id) o.selected = true;
          input.cosmeticSelect.appendChild(o);
        }
        status = `Exclusive “${created.name}” ready — save the level to attach it.`;
        close();
      },
    });

    if (input.editCosmeticId) {
      void (async () => {
        try {
          const { cosmetic } = await fetchAdminBattlePassCosmetic(input.editCosmeticId!);
          const editItem: ShopCreatorEditItem = {
            id: cosmetic.id,
            name: cosmetic.name,
            imageData: cosmetic.imageData,
            outlineMaskData: (cosmetic.outlineMaskData as string | null) ?? null,
            animationFrames: (cosmetic.animationFrames as string[] | null) ?? null,
            category: (cosmetic.category as CosmeticCategory | null) ?? null,
            trailMode: (cosmetic.trailMode as ShopCreatorEditItem["trailMode"]) ?? null,
            trailColors: (cosmetic.trailColors as string[] | null) ?? null,
            themeData: (cosmetic.themeData as ShopCreatorEditItem["themeData"]) ?? null,
            allowCustomColor: !!cosmetic.allowCustomColor,
            allowRepurchase: !!cosmetic.allowRepurchase,
            matchCursorColor: !!cosmetic.matchCursorColor,
            cursorColorShade: Number(cosmetic.cursorColorShade) || 0,
            matchCursorMaskData: (cosmetic.matchCursorMaskData as string | null) ?? null,
            matchCursorMaskFrames: (cosmetic.matchCursorMaskFrames as string[] | null) ?? null,
            unlockType: (cosmetic.unlockType as ShopUnlockType) || "free",
            newCursor: !!cosmetic.newCursor,
          };
          creator.loadPublishedItem(editItem);
        } catch (e) {
          status = e instanceof Error ? e.message : "Could not load exclusive item.";
          close();
        }
      })();
    }
  }

  const render = () => {
    root.replaceChildren();
    root.append(el("h3", "adminModerationHeading", "Battle Pass"), el("p", "accessHint", status || "Create seasons, fill 99 FREE / PREMIUM rewards, then publish."));

    const list = el("div", "adminBpSeasonList");
    for (const s of seasons) {
      const btn = el("button", `btn adminBpSeasonBtn${selected?.id === s.id ? " green" : ""}`, `S${s.seasonNumber} · ${s.name} · ${s.status}`);
      btn.type = "button";
      btn.onclick = () => void openSeason(s.id);
      list.appendChild(btn);
    }
    const createBtn = el("button", "btn green", "New season");
    createBtn.type = "button";
    createBtn.onclick = () => void onCreate();
    list.appendChild(createBtn);
    root.appendChild(list);

    if (!selected) return;

    const tabs = el("div", "adminBpTabs");
    for (const id of ["details", "xp", "levels", "preview"] as const) {
      const btn = el("button", `btn${tab === id ? " green" : ""}`, id.toUpperCase());
      btn.type = "button";
      btn.onclick = () => {
        tab = id;
        render();
      };
      tabs.appendChild(btn);
    }
    root.appendChild(tabs);
    if (warnings.length) {
      const warn = el("div", "adminBpWarnings");
      for (const w of warnings) warn.appendChild(el("p", undefined, w));
      root.appendChild(warn);
    }
    if (tab === "details") renderDetails();
    else if (tab === "xp") renderXp();
    else if (tab === "levels") renderLevels();
    else renderPreview();
  };

  function renderDetails() {
    const s = selected!;
    const box = el("div", "modeBox adminBpEditor");
    const num = numInput(s.seasonNumber, 1, 9999);
    const name = textInput(s.name, "Season name");
    const desc = document.createElement("textarea");
    desc.className = "formInput";
    desc.rows = 3;
    desc.value = s.description || "";
    const price = numInput(s.premiumPriceDots, 1);
    const img = document.createElement("input");
    img.type = "file";
    img.accept = "image/png,image/jpeg,image/webp";
    box.append(
      field("Season number", num),
      field("Name", name),
      field("Description", desc),
      field("Premium price (Dots)", price),
      field("Season art", img),
    );
    if (s.imageData) {
      const preview = document.createElement("img");
      preview.className = "adminBpSeasonArt";
      preview.src = s.imageData;
      box.appendChild(preview);
    }
    const save = el("button", "btn green", "Save details");
    save.type = "button";
    save.onclick = async () => {
      let imageData: string | undefined;
      if (img.files?.[0]) imageData = await fileToDataUrl(img.files[0]);
      await patchAdminBattlePassSeason(s.id, {
        seasonNumber: Number(num.value),
        name: name.value,
        description: desc.value,
        premiumPriceDots: Number(price.value),
        ...(imageData ? { imageData } : {}),
      });
      status = "Details saved.";
      await openSeason(s.id);
    };
    const pub = el("button", "btn green", "Publish season");
    pub.type = "button";
    pub.onclick = () => void onPublish(false);
    const force = el("button", "btn", "Publish anyway");
    force.type = "button";
    force.onclick = () => void onPublish(true);
    const end = el("button", "btn", "End season");
    end.type = "button";
    end.onclick = async () => {
      if (!window.confirm("End this season? Players will stop seeing it as the active pass.")) return;
      await endAdminBattlePassSeason(s.id);
      status = "Season ended.";
      await refresh();
    };
    const del = el("button", "btn", "Delete draft");
    del.type = "button";
    del.onclick = async () => {
      if (s.status !== "draft") return;
      if (!window.confirm("Delete this draft season?")) return;
      await deleteAdminBattlePassSeason(s.id);
      selected = null;
      status = "Draft deleted.";
      await refresh();
    };
    box.append(save, pub, force);
    if (s.status === "published") box.appendChild(end);
    if (s.status === "draft") box.appendChild(del);
    root.appendChild(box);
  }

  function renderXp() {
    const s = selected!;
    const box = el("div", "modeBox adminBpEditor");
    const play = numInput(s.xpPlay);
    const win = numInput(s.xpWin);
    const rec = numInput(s.xpRecord);
    const task = numInput(s.xpTask);
    const base = numInput(s.xpBase, 1);
    const growth = numInput(s.xpGrowthBps, 0, 5000);
    box.append(
      el("p", "accessHint", "Play / win / record XP are granted once per mode per UTC day. Task XP is granted when a player claims an existing Task."),
      field("XP per match played", play),
      field("XP per win / goal clear", win),
      field("XP per personal best", rec),
      field("XP per Task claim", task),
      field("XP for level 1 → 2", base),
      field("Growth (basis points, 400 = +4%/level)", growth),
    );
    const sample = el("p", "accessHint");
    const updateSample = () => {
      const b = Math.max(1, Number(base.value) || 100);
      const g = Math.max(0, Number(growth.value) || 0);
      const need = (n: number) => Math.max(1, Math.floor(b * (1 + ((n - 1) * g) / 10000)));
      sample.textContent = `Sample: Lv2 ${need(1)} XP · Lv10 ${need(10)} XP · Lv50 ${need(50)} XP · Lv99 ${need(99)} XP. Levels 100+ keep using the same formula with no extra rewards.`;
    };
    updateSample();
    base.oninput = updateSample;
    growth.oninput = updateSample;
    const save = el("button", "btn green", "Save XP");
    save.type = "button";
    save.onclick = async () => {
      await patchAdminBattlePassSeason(s.id, {
        xpPlay: Number(play.value),
        xpWin: Number(win.value),
        xpRecord: Number(rec.value),
        xpTask: Number(task.value),
        xpBase: Number(base.value),
        xpGrowthBps: Number(growth.value),
      });
      status = "XP settings saved.";
      await openSeason(s.id);
    };
    box.append(sample, save);
    root.appendChild(box);
  }

  function renderLevels() {
    const wrap = el("div", "adminBpLevels");
    const nav = el("div", "adminBpLevelNav");
    for (const row of levels) {
      const btn = el("button", `adminBpLevelDot${row.incomplete ? " adminBpLevelDot--empty" : ""}${row.level === editLevel ? " is-active" : ""}`, String(row.level));
      btn.type = "button";
      btn.title = row.incomplete ? `Level ${row.level} incomplete` : `Level ${row.level}`;
      btn.onclick = () => {
        editLevel = row.level;
        render();
      };
      nav.appendChild(btn);
    }
    wrap.append(el("p", "accessHint", "Yellow dots still need a FREE or PREMIUM reward."), nav);

    const row = levels.find((l) => l.level === editLevel) || levels[0];
    if (!row) {
      root.appendChild(wrap);
      return;
    }
    const editor = el("div", "modeBox adminBpLevelEditor");
    editor.appendChild(el("h4", undefined, `Level ${row.level}`));
    editor.appendChild(rewardEditor("FREE", "free", row));
    editor.appendChild(rewardEditor("PREMIUM", "premium", row));
    const save = el("button", "btn green", "Save level");
    save.type = "button";
    save.onclick = () => void saveLevel(row, editor);
    const prev = el("button", "btn", "Prev");
    prev.type = "button";
    prev.onclick = () => {
      editLevel = Math.max(1, editLevel - 1);
      render();
    };
    const next = el("button", "btn", "Next");
    next.type = "button";
    next.onclick = () => {
      editLevel = Math.min(99, editLevel + 1);
      render();
    };
    editor.append(save, prev, next);
    wrap.appendChild(editor);
    root.appendChild(wrap);
  }

  function rewardEditor(title: string, prefix: "free" | "premium", row: BattlePassLevelAdmin): HTMLElement {
    const box = el("section", `adminBpRewardBox adminBpRewardBox--${prefix}`);
    box.appendChild(el("h5", undefined, `${title} REWARD`));
    const kind = document.createElement("select");
    kind.className = "formInput";
    const currentKind = prefix === "free" ? row.freeRewardKind : row.premiumRewardKind;
    for (const opt of REWARD_KINDS) {
      const o = document.createElement("option");
      o.value = opt.id;
      o.textContent = opt.label;
      if (opt.id === currentKind) o.selected = true;
      kind.appendChild(o);
    }
    const dots = numInput(prefix === "free" ? row.freeRewardDots : row.premiumRewardDots);
    const cosmetic = document.createElement("select");
    cosmetic.className = "formInput";
    const crate = document.createElement("select");
    crate.className = "formInput";
    const fillSelect = (
      select: HTMLSelectElement,
      items: Array<{ id: string; name: string }>,
      current: string | null,
    ) => {
      select.replaceChildren();
      const empty = document.createElement("option");
      empty.value = "";
      empty.textContent = "—";
      select.appendChild(empty);
      for (const item of items) {
        const o = document.createElement("option");
        o.value = item.id;
        o.textContent = item.name;
        if (item.id === current) o.selected = true;
        select.appendChild(o);
      }
    };
    fillSelect(
      cosmetic,
      kind.value === "exclusive_item" ? pickers.exclusives : pickers.shop,
      prefix === "free" ? row.freeRewardCosmeticId : row.premiumRewardCosmeticId,
    );
    fillSelect(crate, pickers.crates, prefix === "free" ? row.freeRewardCrateId : row.premiumRewardCrateId);
    const exclusiveRow = el("div", "formRow adminBpExclusiveTools hidden");
    const createExclusiveBtn = el("button", "btn green", "Create exclusive item");
    createExclusiveBtn.type = "button";
    const editExclusiveBtn = el("button", "btn", "Edit item");
    editExclusiveBtn.type = "button";
    const syncExclusiveTools = () => {
      const show = kind.value === "exclusive_item";
      exclusiveRow.classList.toggle("hidden", !show);
      editExclusiveBtn.disabled = !cosmetic.value;
    };
    kind.onchange = () => {
      fillSelect(
        cosmetic,
        kind.value === "exclusive_item" ? pickers.exclusives : pickers.shop,
        cosmetic.value,
      );
      syncExclusiveTools();
    };
    cosmetic.onchange = () => syncExclusiveTools();
    const preview = prefix === "free" ? row.free : row.premium;
    const prev = el("div", "adminBpRewardPreview");
    if (preview.imageData) {
      const img = document.createElement("img");
      img.src = preview.imageData;
      prev.appendChild(img);
    }
    prev.appendChild(el("span", undefined, preview.label));
    box.append(
      field("Type", kind),
      field("Dots", dots),
      field("Item", cosmetic),
      field("Crate", crate),
      prev,
    );
    createExclusiveBtn.onclick = () =>
      openExclusiveItemCreator({
        levelId: row.id,
        cosmeticSelect: cosmetic,
        kindSelect: kind,
      });
    editExclusiveBtn.onclick = () =>
      openExclusiveItemCreator({
        levelId: row.id,
        cosmeticSelect: cosmetic,
        kindSelect: kind,
        editCosmeticId: cosmetic.value || null,
      });
    const exclusiveActions = el("div", "rgbRow");
    exclusiveActions.append(createExclusiveBtn, editExclusiveBtn);
    exclusiveRow.append(
      el("span", "formLabel", "Exclusive item"),
      exclusiveActions,
    );
    box.appendChild(exclusiveRow);
    syncExclusiveTools();
    box.dataset.prefix = prefix;
    (box as HTMLElement & { kindEl?: HTMLSelectElement }).kindEl = kind;
    box.append;
    kind.dataset.role = "kind";
    dots.dataset.role = "dots";
    cosmetic.dataset.role = "cosmetic";
    crate.dataset.role = "crate";
    return box;
  }

  async function saveLevel(row: BattlePassLevelAdmin, editor: HTMLElement) {
    const readBox = (prefix: "free" | "premium") => {
      const box = editor.querySelector(`.adminBpRewardBox--${prefix}`) as HTMLElement | null;
      const kind = (box?.querySelector('[data-role="kind"]') as HTMLSelectElement | null)?.value || "none";
      const dots = Number((box?.querySelector('[data-role="dots"]') as HTMLInputElement | null)?.value || 0);
      const cosmetic = (box?.querySelector('[data-role="cosmetic"]') as HTMLSelectElement | null)?.value || null;
      const crate = (box?.querySelector('[data-role="crate"]') as HTMLSelectElement | null)?.value || null;
      return {
        [`${prefix}RewardKind`]: kind,
        [`${prefix}RewardDots`]: dots,
        [`${prefix}RewardCosmeticId`]: cosmetic,
        [`${prefix}RewardCrateId`]: crate,
      };
    };
    await patchAdminBattlePassLevel(row.seasonId, row.level, {
      ...readBox("free"),
      ...readBox("premium"),
    });
    status = `Level ${row.level} saved.`;
    await openSeason(row.seasonId);
  }

  function renderPreview() {
    const s = selected!;
    const box = el("div", "adminBpPreview");
    box.append(
      el("p", "bpSeasonKicker", `SEASON ${s.seasonNumber}`),
      el("h4", undefined, s.name),
      el("p", "accessHint", s.description || "No description"),
      el("p", undefined, `Premium: ${s.premiumPriceDots.toLocaleString()} Dots · Status: ${s.status}`),
    );
    const track = el("div", "bpTrack adminBpPreviewTrack");
    for (const row of levels) {
      const col = el("article", "bpCol");
      const prem = el("div", `bpReward bpReward--premium${row.premium.kind === "none" ? " bpReward--empty" : ""}`);
      prem.textContent = row.premium.label;
      const mid = el("div", "bpColLevel", String(row.level));
      const free = el("div", `bpReward bpReward--free${row.free.kind === "none" ? " bpReward--empty" : ""}`);
      free.textContent = row.free.label;
      col.append(prem, mid, free);
      track.appendChild(col);
    }
    box.appendChild(track);
    root.appendChild(box);
  }

  async function onCreate() {
    const created = await createAdminBattlePassSeason({});
    status = `Created Season ${created.season.seasonNumber}.`;
    await refresh();
    await openSeason(created.season.id);
  }

  async function onPublish(force: boolean) {
    if (!selected) return;
    try {
      await publishAdminBattlePassSeason(selected.id, force);
      status = force ? "Season published (warnings ignored)." : "Season published.";
      await refresh();
      await openSeason(selected.id);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "publish_failed";
      status = `Cannot publish: ${msg}. Fill missing rewards or use Publish anyway.`;
      render();
    }
  }

  async function openSeason(id: string) {
    const data = await fetchAdminBattlePassSeason(id);
    selected = data.season;
    levels = data.levels;
    warnings = data.warnings || [];
    if (!levels.some((l) => l.level === editLevel)) editLevel = 1;
    render();
  }

  async function refresh() {
    seasons = await fetchAdminBattlePassSeasons();
    const [ex, shop, crates] = await Promise.all([
      fetchAdminBattlePassPickers("cosmetics"),
      fetchAdminBattlePassPickers("shop-items"),
      fetchAdminBattlePassPickers("crates"),
    ]);
    pickers = {
      exclusives: ex.cosmetics ?? [],
      shop: shop.cosmetics ?? [],
      crates: crates.crates ?? [],
    };
    if (selected) {
      const still = seasons.find((s) => s.id === selected?.id);
      if (still) await openSeason(still.id);
      else {
        selected = null;
        render();
      }
    } else {
      render();
    }
  }

  void refresh();
  return { refresh };
}
